import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { toast } from 'sonner';

export interface EndpointTestPanelProps {
  sessionId: string;
  recipeYaml: string;
  onPatchProposed: (newYaml: string, summary: string) => void;
}

// List endpoint names from the recipe YAML by walking the indented `endpoints:` block.
// The endpoint names are the keys two spaces deep — anything else (nested keys like
// `headers:`, `pagination:`, etc.) lives at four+ spaces.
function listEndpoints(yamlText: string): string[] {
  const m = /\nendpoints:\s*\n([\s\S]*?)(?:\n[a-z][a-zA-Z]*:|\n*$)/.exec(`\n${yamlText}`);
  if (!m) return [];
  const block = m[1] ?? '';
  const names: string[] = [];
  // Match lines starting with exactly two spaces, an identifier, and a colon.
  const re = /^ {2}([a-zA-Z_][a-zA-Z0-9_-]*):/gm;
  let mm: RegExpExecArray | null;
  while ((mm = re.exec(block)) !== null) {
    if (mm[1]) names.push(mm[1]);
  }
  return names;
}

interface TestResultState {
  status: 'ok' | 'fetch_failed' | 'transform_failed' | 'auth_failed';
  recordCount: number;
  fetchStatus?: number;
  fetchError?: string;
  transformError?: string;
  validationIssues?: Array<{ path: string; message: string }>;
  rawRecord?: unknown;
  transformedSample?: unknown;
  rawSample?: string;
}

const STATUS_BADGES: Record<TestResultState['status'] | 'untested', { bg: string; text: string; label: string }> = {
  untested: { bg: 'rgba(148,163,184,0.12)', text: 'var(--text-dimmed)', label: 'untested' },
  ok: { bg: 'rgba(13,207,184,0.22)', text: 'rgb(94,234,212)', label: 'ok' },
  fetch_failed: { bg: 'rgba(244,63,94,0.22)', text: 'rgb(253,164,175)', label: 'fetch fail' },
  transform_failed: { bg: 'rgba(251,146,60,0.22)', text: 'rgb(253,186,116)', label: 'xform fail' },
  auth_failed: { bg: 'rgba(251,146,60,0.22)', text: 'rgb(253,186,116)', label: 'auth fail' },
};

export function EndpointTestPanel({ sessionId, recipeYaml, onPatchProposed }: EndpointTestPanelProps) {
  const endpoints = useMemo(() => listEndpoints(recipeYaml), [recipeYaml]);
  const [results, setResults] = useState<Record<string, TestResultState>>({});
  const [busy, setBusy] = useState<Record<string, 'test' | 'repair' | undefined>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const test = useMutation({
    mutationFn: async ({ name }: { name: string }) => {
      const r = await api.testScopeEndpoint(sessionId, name, recipeYaml);
      return { name, r };
    },
    onMutate: ({ name }) => setBusy((p) => ({ ...p, [name]: 'test' })),
    onSettled: (_d, _e, v) => setBusy((p) => ({ ...p, [v.name]: undefined })),
    onSuccess: ({ name, r }) => {
      let status: TestResultState['status'] = 'ok';
      if (r.authFailed) status = 'auth_failed';
      else if (r.fetchError) status = 'fetch_failed';
      else if (r.transform && !r.transform.ok) status = 'transform_failed';
      setResults((p) => ({
        ...p,
        [name]: {
          status,
          recordCount: r.recordCount,
          fetchStatus: r.fetchStatus,
          fetchError: r.fetchError,
          transformError: r.transform?.error,
          validationIssues: r.transform?.validationIssues,
          rawRecord: r.transform?.rawRecord,
          transformedSample: r.transform?.output,
          rawSample: r.rawSample,
        },
      }));
    },
    onError: (err) => toast.error(`Test failed: ${err instanceof Error ? err.message : String(err)}`),
  });

  const repair = useMutation({
    mutationFn: async ({ name, current }: { name: string; current: TestResultState }) => {
      const r = await api.repairScopeEndpoint(sessionId, {
        endpointName: name,
        recipeYaml,
        failure: {
          fetchStatus: current.fetchStatus,
          fetchError: current.fetchError,
          rawSample: current.rawSample,
          validationIssues: current.validationIssues,
        },
        exampleRecord: current.rawRecord,
      });
      return { name, r };
    },
    onMutate: ({ name }) => setBusy((p) => ({ ...p, [name]: 'repair' })),
    onSettled: (_d, _e, v) => setBusy((p) => ({ ...p, [v.name]: undefined })),
    onSuccess: ({ name, r }) => {
      onPatchProposed(r.patchedYaml, r.rationale);
      toast.success(`Patch proposed for ${name} — review the YAML in the editor`);
    },
    onError: (err) => toast.error(`Repair failed: ${err instanceof Error ? err.message : String(err)}`),
  });

  if (endpoints.length === 0) {
    return null;
  }

  return (
    <div className="rounded-xl p-3" style={{ border: '1px solid var(--glass-border)' }}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-semibold uppercase tracking-widest text-text-dimmed">Per-Endpoint Tools</div>
        <div className="text-[10px] text-text-dimmed">
          Test fetches one page with live auth; AI Repair proposes a minimal patch
        </div>
      </div>
      <div className="space-y-1">
        {endpoints.map((name) => {
          const r = results[name];
          const b = busy[name];
          const badge = r ? STATUS_BADGES[r.status] : STATUS_BADGES.untested;
          const isExpanded = !!expanded[name];
          const hasFailure = r && r.status !== 'ok';
          return (
            <div
              key={name}
              className="rounded-lg px-3 py-2 text-sm"
              style={{ background: 'rgba(0,0,0,0.18)', border: '1px solid rgba(148,163,184,0.12)' }}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <span
                    className="font-mono text-xs uppercase tracking-wide px-2 py-0.5 rounded"
                    style={{ color: badge.text, background: badge.bg }}
                  >
                    {badge.label}
                  </span>
                  <span className="font-medium truncate">{name}</span>
                  {r && r.status === 'ok' && (
                    <span className="text-xs text-emerald-300 truncate">
                      {r.recordCount} record{r.recordCount === 1 ? '' : 's'}, transform ok
                    </span>
                  )}
                  {r && r.status === 'fetch_failed' && (
                    <span className="text-xs text-rose-300 truncate" title={r.fetchError}>
                      HTTP {r.fetchStatus ?? '?'} — {(r.fetchError ?? '').slice(0, 80)}
                    </span>
                  )}
                  {r && r.status === 'transform_failed' && (
                    <span className="text-xs text-amber-300 truncate" title={r.transformError}>
                      {r.recordCount} fetched · transform error
                    </span>
                  )}
                </div>
                <div className="flex gap-2 shrink-0">
                  {r && (
                    <button
                      type="button"
                      onClick={() => setExpanded((p) => ({ ...p, [name]: !p[name] }))}
                      className="rounded px-2 py-1 text-xs font-semibold"
                      style={{ background: 'rgba(148,163,184,0.18)', color: 'var(--text-dimmed)', border: '1px solid rgba(148,163,184,0.3)' }}
                    >
                      {isExpanded ? 'Hide' : 'Detail'}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => test.mutate({ name })}
                    disabled={!!b}
                    className="rounded px-2 py-1 text-xs font-semibold transition-opacity disabled:opacity-50"
                    style={{
                      background: 'rgba(56,189,248,0.18)',
                      color: 'rgb(125,211,252)',
                      border: '1px solid rgba(56,189,248,0.45)',
                    }}
                  >
                    {b === 'test' ? 'Testing…' : 'Test'}
                  </button>
                  {hasFailure && (
                    <button
                      type="button"
                      onClick={() => repair.mutate({ name, current: r })}
                      disabled={!!b}
                      className="rounded px-2 py-1 text-xs font-semibold transition-opacity disabled:opacity-50"
                      style={{
                        background: 'rgba(168,85,247,0.18)',
                        color: 'rgb(216,180,254)',
                        border: '1px solid rgba(168,85,247,0.45)',
                      }}
                    >
                      {b === 'repair' ? 'Repairing…' : '✨ AI Repair'}
                    </button>
                  )}
                </div>
              </div>

              {isExpanded && r && (
                <div className="mt-2 space-y-2 text-xs">
                  {r.fetchError && (
                    <div className="rounded bg-rose-900/20 px-2 py-1.5 border border-rose-700/40">
                      <div className="font-semibold text-rose-200 mb-0.5">Fetch error</div>
                      <div className="font-mono text-rose-300 whitespace-pre-wrap break-words">
                        {r.fetchError}
                      </div>
                    </div>
                  )}
                  {r.validationIssues && r.validationIssues.length > 0 && (
                    <div className="rounded bg-amber-900/20 px-2 py-1.5 border border-amber-700/40">
                      <div className="font-semibold text-amber-200 mb-0.5">Validation errors ({r.validationIssues.length})</div>
                      <ul className="font-mono text-amber-300 space-y-0.5">
                        {r.validationIssues.slice(0, 8).map((v, i) => (
                          <li key={i}>
                            <span className="opacity-70">{v.path || '(root)'}:</span> {v.message}
                          </li>
                        ))}
                        {r.validationIssues.length > 8 && (
                          <li className="opacity-70">… and {r.validationIssues.length - 8} more</li>
                        )}
                      </ul>
                    </div>
                  )}
                  {r.rawSample && (
                    <details>
                      <summary className="cursor-pointer text-text-dimmed">Raw response sample</summary>
                      <pre className="mt-1 font-mono text-[11px] text-text-secondary bg-black/20 rounded p-2 overflow-x-auto whitespace-pre-wrap break-words max-h-40">
                        {r.rawSample.slice(0, 2000)}
                      </pre>
                    </details>
                  )}
                  {r.rawRecord !== undefined && r.rawRecord !== null && (
                    <details>
                      <summary className="cursor-pointer text-text-dimmed">First raw record</summary>
                      <pre className="mt-1 font-mono text-[11px] text-text-secondary bg-black/20 rounded p-2 overflow-x-auto max-h-40">
                        {JSON.stringify(r.rawRecord, null, 2).slice(0, 2000)}
                      </pre>
                    </details>
                  )}
                  {r.transformedSample !== undefined && (
                    <details>
                      <summary className="cursor-pointer text-text-dimmed">Transform output</summary>
                      <pre className="mt-1 font-mono text-[11px] text-text-secondary bg-black/20 rounded p-2 overflow-x-auto max-h-40">
                        {JSON.stringify(r.transformedSample, null, 2).slice(0, 2000)}
                      </pre>
                    </details>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
